import React, { useCallback, useRef, useLayoutEffect, useEffect, useState, useMemo } from 'react';
import { Formik, Form, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../../../reduxService/hooks';
import Dialog from '../../../../components/Dialog/Dialog';
import FieldRenderer from './FieldRenderer';
import { renderErrorMessage } from './FieldRenderer/utils';
import {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getLayerCoefficientForSurfaceMaterial,
    getMinimumSuggestedThicknessForSurface,
    getMinimumSuggestedThicknessForBase,
    getMinimumSuggestedThicknessForSubbase,
} from '../../../services/ProjectTypeService';
import { submitFailed, submitSucceeded } from '../../../store/form/formControlSlice';
import { unit as unitEnum } from '../../../enum/project';
import { FieldConfig } from '../../../services/ValidationService';
import { useProjectFormSetupModular as useProjectFormSetup } from '../../hooks/useProjectFormSetupModular';
import { useFormSubmission } from '../../hooks/useFormSubmission';
import { createErrorPayload } from '../../utils/formSubmissionHelpers';
import FormSection from './FormSection';
import { setProject, updateDesignEnableFlags } from '../../../Dynamic/store/project/projectslice';
import { logDebug, logWarn, logError, LogTag } from '../../../utils/utils';
import { DynamicFormikProps, FieldGroupProps } from '../../../types/FormTypes';
import { useFieldConversions, useConditionalFields } from './FieldUtils';
import { convertFlatData } from '../../../services/UnitConversionService';
import { FORM_SUBMIT_CONVERSION_SOURCE, isConversionNeeded, logConversionDecision } from '../../../utils/conversionUtils';
import { useFormCascade } from '../../../hooks/useFormMaterialCascade';
import { useFormTrafficCascade } from '../../../hooks/useFormTrafficCascade';
import { useMaterialCascadeInit } from '../../hooks/useMaterialCascadeInit';
import EsalCalculationModal from './EsalCalculationModal';
import EsalCalculationModalAirport from './EsalCalculationModalAirport';
import { useFormStateManagement } from '../../../hooks/useFormStateManagement';
import { useEsalModalManagement } from '../../../hooks/useEsalModalManagement';
import { useFormEffects } from '../../hooks/useFormEffects';
import { usePerformanceOptimization } from './hooks/usePerformanceOptimization';
import { useMemoizedHandlers } from './hooks/useMemoizedHandlers';
import { useAccordionManagement } from '../../../hooks/useAccordionManagement';

// Type for form values extending basic form structure
type ProjectFormValues = Record<string, unknown>;

// Type for material cascade hook return value
type MaterialCascadeHook = {
    handleBaseMaterialChange: (value: string) => void;
    handleSubbaseMaterialChange: (value: string) => void;
    handleSurfaceMaterialChange: (value: string) => void;
    handleBaseGeogridChange: (value: string) => void;
    handleSubbaseGeogridChange: (value: string) => void;
    handleRailwayGeogridChange: (value: string) => void;
    initializeFormMaterials: () => void;
    // New centralized handlers
    handleFieldChange: (fieldName: string, value: string, setFieldValue: (field: string, value: any) => void, values?: any) => void;
    isFieldCascadable: (fieldName: string) => boolean;
};
import { useRailwayConditionalDefaults } from '../../../Dynamic/hooks/useRailwayConditionalDefaults';
import { compareFormValues } from '../../../utils/performanceUtils';
import { useFormikSyncToContext } from '../../../hooks/useFormikSyncToContext';
import { IProject } from '../../../api/IApiResponse';
import { IInitialState, redux } from '../../../../reduxService';

import './InputForm.scss';

// Re-export components for backward compatibility
export const StyledErrorMessage: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <div className="error formik-error" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
        {children}
    </div>
);

// 🔧 FIX: Separate component to handle hooks inside Formik render prop
interface FormikInnerComponentProps {
    setFieldValue: (field: string, value: unknown) => void; // Formik setFieldValue function
    values: unknown; // Formik form values object
    dirty: boolean;
    initialValues: unknown; // Formik initial values object
    updateFormState: (dirty: boolean, values: unknown, initialValues: unknown) => void; // form state update function with correct signature
    materialCascade: MaterialCascadeHook; // material cascade data object
    initializeOnce: (data: unknown) => void; // initialization function
    dynamicProject: IInitialState<IProject>; // dynamic project data object
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setFieldValue?: (field: string, value: unknown) => void, fieldName?: string, formValues?: Record<string, any>) => void; // input change handler
    projectDataFromState: IProject | undefined; // project data from Redux state
    projectUnitFromState: string; // unit system from project state
    validatedData: unknown; // validated form data object
    getMeasurementUnit: (unit: string[] | undefined) => string; // measurement unit formatter function
    handleAccordionToggle: (accordionId: string) => void; // accordion toggle handler
    handleEsalModalClose: () => void; // modal close handler
    handleEsalCalculationComplete: (result: number | { esalFinalValue: number; totalPassagesPerYear: number }) => void; // ESAL calculation completion handler
    groupedFields: Record<string, FieldConfig[]>; // field configurations grouped by section name
    displayMethod: string;
    accordionTitles: Record<string, string>; // translation keys for accordion titles
    t: (key: string, options?: unknown) => string; // i18n translation function
    isNewProject: boolean;
    openAccordions: Record<string, boolean>; // accordion state tracking
    registerMethodSyncCallback: (callback: (method: string) => void) => void; // method sync callback registration
    updateGroupedFieldsForDesignChange: (formValues: any) => Promise<void>; // function to update grouped fields when design features change
    onApplyingDefaultsChange: (isApplying: boolean) => void; // callback for railway defaults application state
    dispatch: ReturnType<typeof useAppDispatch>; // Redux dispatch function
}

const FormikInnerComponent: React.FC<FormikInnerComponentProps> = ({
    setFieldValue,
    values,
    dirty,
    initialValues,
    updateFormState,
    materialCascade,
    initializeOnce,
    dynamicProject,
    handleInputChange,
    projectUnitFromState,
    getMeasurementUnit,
    handleAccordionToggle,
    handleEsalModalClose,
    handleEsalCalculationComplete,
    groupedFields,
    displayMethod,
    accordionTitles,
    t,
    isNewProject,
    openAccordions,
    registerMethodSyncCallback,
    updateGroupedFieldsForDesignChange,
    onApplyingDefaultsChange,
    dispatch,
}) => {
    // Component interno per sincronizzare valori Formik con FormValuesContext
    const FormikSyncComponent = () => {
        useFormikSyncToContext(groupedFields);
        return null;
    };

    // 🔧 FIX: Use ref to track latest form values
    // This ensures method change callbacks have access to current values (not stale closure)
    const valuesRef = React.useRef(values);
    React.useEffect(() => {
        valuesRef.current = values;
    }, [values]);

    // BUG FIX: Register method sync callback for dropdown synchronization
    React.useEffect(() => {
        const syncCallback = (method: string) => {
            setFieldValue('projectSettings.calculationMethod', method);
        };
        registerMethodSyncCallback(syncCallback);
    }, [setFieldValue, registerMethodSyncCallback]);

    // Update form state using our custom hook (moved to useEffect to avoid setState during render)
    useEffect(() => {
        // 🔍 DEBUG: Log Formik's actual values vs initialValues
        console.log('🔍 DEBUG: FormikInnerComponent - values vs initialValues', {
            surfaceMinThicknessInValues: (values as any)?.surface?.minThickness,
            surfaceMinThicknessInInitialValues: (initialValues as any)?.surface?.minThickness,
            dirty,
        });
        updateFormState(dirty, values, initialValues);
    }, [dirty, values, initialValues, updateFormState]);

    // 🔧 RAILWAY: Hook for conditional default values based on railway type selection
    const { isApplyingDefaults } = useRailwayConditionalDefaults(values, setFieldValue, dynamicProject.data?.type || '');

    // 🔧 TRAFFIC: Hook for traffic cascade (reliability level suggested updates)
    // Need to create a formik-like object for the hook
    const formikForTrafficCascade = React.useMemo(
        () => ({
            setFieldValue,
            values,
        }),
        [setFieldValue, values]
    );
    const trafficCascade = useFormTrafficCascade(formikForTrafficCascade as any);

    // Notify parent component about defaults application state
    useEffect(() => {
        onApplyingDefaultsChange(isApplyingDefaults);
    }, [isApplyingDefaults, onApplyingDefaultsChange]);

    // 🔧 FIX: Stabilized material cascade initialization
    useLayoutEffect(() => {
        if (values && materialCascade) {
            initializeOnce(materialCascade);
        }
    }, [values, materialCascade, initializeOnce]);

    // NOTE: Monitor design toggles and update accordion visibility immediately
    // Uses Formik values directly instead of waiting for Redux state updates
    // 🔧 FIX: Also dispatch to Redux immediately so Results_Paved can see the changes
    useEffect(() => {
        const formValues = values as any;
        const currentSurfaceEnabled = formValues?.surfaceDesign?.enableSurfaceDesign === 'yes';
        const currentBaseEnabled = formValues?.baseDesign?.enableBaseDesign === 'yes';
        const currentSubbaseEnabled = formValues?.subbaseDesign?.enableSubbaseDesign === 'yes';

        const hasAnyDesignEnabled = currentSurfaceEnabled || currentBaseEnabled || currentSubbaseEnabled;
        const currentProjectType = dynamicProject?.data?.type;
        const isPaved = currentProjectType === 'paved' || currentProjectType === 'airport';

        // 🔧 FIX: Dispatch to Redux immediately when enable flags change
        // This allows Results_Paved to render canvas immediately without waiting for form submission
        if (isPaved && values) {
            const surfaceValue = formValues?.surfaceDesign?.enableSurfaceDesign;
            const baseValue = formValues?.baseDesign?.enableBaseDesign;
            const subbaseValue = formValues?.subbaseDesign?.enableSubbaseDesign;

            if (surfaceValue) {
                dispatch(updateDesignEnableFlags({ section: 'surfaceDesign', enabled: surfaceValue }));
            }
            if (baseValue) {
                dispatch(updateDesignEnableFlags({ section: 'baseDesign', enabled: baseValue }));
            }
            if (subbaseValue) {
                dispatch(updateDesignEnableFlags({ section: 'subbaseDesign', enabled: subbaseValue }));
            }
        }

        // Only trigger for paved/airport projects when design features are enabled
        if (isPaved && hasAnyDesignEnabled && values) {
            updateGroupedFieldsForDesignChange(values).catch(error => {
                logError('InputForm:REACTIVE', 'Failed to update grouped fields for design change', error);
            });
        }
    }, [
        (values as any)?.surfaceDesign?.enableSurfaceDesign,
        (values as any)?.baseDesign?.enableBaseDesign,
        (values as any)?.subbaseDesign?.enableSubbaseDesign,
        dynamicProject?.data?.type,
        updateGroupedFieldsForDesignChange,
        dispatch,
    ]);

    // 🎯 Enhanced input change handler - now using centralized cascade logic
    const enhancedInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setField: (field: string, value: unknown) => void, fieldName: string) => {
            const { value } = e.target;

            logDebug('InputForm', `🔍 ENHANCED HANDLER CALLED: fieldName=${fieldName}, value=${value}`, undefined, LogTag.FORM_VALIDATION);

            // 🎯 Use centralized material cascade handling from hook
            if (materialCascade.isFieldCascadable(fieldName)) {
                logDebug('InputForm', `🎯 Material cascade triggered for: ${fieldName}`, undefined, LogTag.FORM_VALIDATION);
                materialCascade.handleFieldChange(fieldName, value as string, setField, valuesRef.current);
            }

            // 🎯 Use traffic cascade handling for roadType and functionalClassification
            if (fieldName === 'roadType' || fieldName === 'functionalClassification') {
                logDebug('InputForm', `🔄 Traffic cascade triggered for: ${fieldName}`, undefined, LogTag.FORM_VALIDATION);
                if (fieldName === 'roadType') {
                    trafficCascade.handleRoadTypeChange(value as string, setField, values as any);
                } else if (fieldName === 'functionalClassification') {
                    trafficCascade.handleFunctionalClassificationChange(value as string, setField, values as any);
                }
            }

            // Handle all field changes through the standard Formik mechanism
            // 🔧 FIX: Use valuesRef.current for method changes to get latest values (not stale closure)
            handleInputChange(e, setField, fieldName, valuesRef.current as Record<string, any>);
        },
        [handleInputChange, materialCascade, trafficCascade, values]
    );

    // Create memoized handlers for performance optimization
    const handlers = useMemoizedHandlers({
        handleInputChange: enhancedInputChange,
        getMeasurementUnit,
        handleAccordionToggle,
        handleEsalModalClose,
        handleEsalCalculationComplete,
    });

    return (
        <Form role="form" data-testid="dynamic-form">
            <FormikSyncComponent />
            {Object.keys(groupedFields).map(groupName => {
                const groupFieldConfigs = groupedFields[groupName];
                const isAccordionOpen = !!openAccordions[groupName];

                return (
                    <FormSection
                        key={`${displayMethod}-${groupName}`}
                        groupName={groupName}
                        fieldConfigs={groupFieldConfigs}
                        displayMethod={displayMethod}
                        accordionTitles={accordionTitles as Record<string, string>}
                        values={values as Record<string, unknown>}
                        setFieldValue={setFieldValue}
                        t={t}
                        handleInputChange={handlers.handleInputChange}
                        getMeasurementUnit={handlers.getMeasurementUnit}
                        isNewProject={isNewProject}
                        unitSystem={projectUnitFromState || 'SI'}
                        isOpen={isAccordionOpen}
                        onToggle={() => handlers.handleAccordionToggle(groupName)}
                    />
                );
            })}
        </Form>
    );
};

// Performance optimized FieldGroup component with React.memo
const FieldGroupComponent: React.FC<FieldGroupProps> = ({ groupName, values, dynamicFields, setFieldValue, handleInputChange, getMeasurementUnit, t, unitSystem }) => {
    // Use utility hooks for conversion and conditional logic
    const { convertAndSetValues } = useFieldConversions(groupName, setFieldValue);
    const { getConditionallyFilteredFields } = useConditionalFields(dynamicFields, groupName, values);

    // Use utility hook for conditional field filtering
    const fieldsToRender = getConditionallyFilteredFields();

    // Use performance optimization hook for expensive computations
    const { memoizedFieldGroups } = usePerformanceOptimization(fieldsToRender);

    // Create adapter for handleInputChange to match FieldRenderer's expected signature
    const adaptedHandleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setFieldValue?: (field: string, value: unknown) => void, fieldName?: string) => {
        if (setFieldValue && fieldName) {
            // Create a compatible setFieldValue wrapper
            const compatibleSetFieldValue = (field: string, value: unknown) => {
                setFieldValue(field, value);
            };
            // Pass values so method change can access current form data
            handleInputChange(e, compatibleSetFieldValue, fieldName, values as Record<string, any>);
        }
    };

    // Create adapter for setFieldValue to match FieldRenderer's expected signature
    const adaptedSetFieldValue = (field: string, value: unknown) => {
        setFieldValue(field, value);
    };

    // Create adapter for t function to match FieldRenderer's expected signature
    const adaptedT = (key: string, options?: Record<string, unknown>) => {
        return t(key, options as Record<string, any>); // eslint-disable-line @typescript-eslint/no-explicit-any
    };

    return (
        <div className="field-group-content">
            {memoizedFieldGroups.map((group, index) => {
                if (Array.isArray(group)) {
                    // Inline field group
                    const [leftField, rightField] = group;
                    return (
                        <div key={`inline-group-${index}`} className="inline-field-group" style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                            {(() => {
                                // Helper to parse width (number or string)
                                const parseWidth = (w: number | string | undefined) => {
                                    if (typeof w === 'number') return w + '%';
                                    if (typeof w === 'string' && w.trim().endsWith('%')) return w.trim();
                                    if (typeof w === 'string' && w.trim() !== '') return w.trim() + '%';
                                    return '50%';
                                };

                                const leftWidth = parseWidth(leftField.layout?.width);
                                const rightWidth = parseWidth(rightField.layout?.width);
                                return (
                                    <>
                                        <div style={{ width: `calc(${leftWidth} - 8px)`, flex: '0 0 auto' }}>
                                            <FieldRenderer
                                                fieldName={leftField.name}
                                                fieldConfig={leftField}
                                                nestedFieldName={`${groupName}.${leftField.name}`}
                                                groupName={groupName}
                                                unitSystem={unitSystem}
                                                values={values}
                                                dynamicFields={dynamicFields}
                                                setFieldValue={adaptedSetFieldValue}
                                                handleInputChange={adaptedHandleInputChange}
                                                getMeasurementUnit={getMeasurementUnit}
                                                t={adaptedT}
                                                convertAndSetValues={convertAndSetValues}
                                                renderErrorMessage={renderErrorMessage}
                                            />
                                        </div>
                                        <div style={{ width: `calc(${rightWidth} - 8px)`, flex: '0 0 auto' }}>
                                            <FieldRenderer
                                                fieldName={rightField.name}
                                                fieldConfig={rightField}
                                                nestedFieldName={`${groupName}.${rightField.name}`}
                                                groupName={groupName}
                                                unitSystem={unitSystem}
                                                values={values}
                                                dynamicFields={dynamicFields}
                                                setFieldValue={adaptedSetFieldValue}
                                                handleInputChange={adaptedHandleInputChange}
                                                getMeasurementUnit={getMeasurementUnit}
                                                t={adaptedT}
                                                convertAndSetValues={convertAndSetValues}
                                                renderErrorMessage={renderErrorMessage}
                                            />
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    );
                } else {
                    // Single field
                    return (
                        <div key={group.name}>
                            <FieldRenderer
                                fieldName={group.name}
                                fieldConfig={group}
                                nestedFieldName={`${groupName}.${group.name}`}
                                groupName={groupName}
                                unitSystem={unitSystem}
                                values={values}
                                dynamicFields={dynamicFields}
                                setFieldValue={adaptedSetFieldValue}
                                handleInputChange={adaptedHandleInputChange}
                                getMeasurementUnit={getMeasurementUnit}
                                t={adaptedT}
                                convertAndSetValues={convertAndSetValues}
                                renderErrorMessage={renderErrorMessage}
                            />
                        </div>
                    );
                }
            })}
        </div>
    );
};

// Memoized FieldGroup with optimized comparison function
export const FieldGroup: React.FC<FieldGroupProps> = React.memo(FieldGroupComponent, (prevProps, nextProps) => {
    // Only re-render if essential props change
    return (
        prevProps.groupName === nextProps.groupName &&
        prevProps.isNewProject === nextProps.isNewProject &&
        prevProps.unitSystem === nextProps.unitSystem &&
        // Use optimized form values comparison instead of JSON.stringify
        compareFormValues(prevProps.values, nextProps.values) &&
        prevProps.dynamicFields === nextProps.dynamicFields &&
        prevProps.setFieldValue === nextProps.setFieldValue &&
        prevProps.handleInputChange === nextProps.handleInputChange &&
        prevProps.getMeasurementUnit === nextProps.getMeasurementUnit &&
        prevProps.t === nextProps.t
    );
});

interface DynamicFormProps {
    formikRef?: React.RefObject<DynamicFormikProps>;
}

const DynamicForm: React.FC<DynamicFormProps> = ({ formikRef: externalFormikRef }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const internalFormikRef = useRef<DynamicFormikProps>(null);
    const formikRef = externalFormikRef || internalFormikRef;
    // Material cascade ref with proper typing
    const materialCascadeFormikRef = useRef<FormikProps<ProjectFormValues> | null>(null);

    const {
        loading,
        errorDialog,
        setErrorDialog,
        groupedFields: rawGroupedFields,
        accordionTitles,
        validationSchema,
        validatedData,
        isMethodChanging,
        handleMethodChange,
        projectDataFromState,
        projectUnitFromState,
        isNewProject,
        registerMethodSyncCallback,
        confirmDialogState,
        updateGroupedFieldsForDesignChange,
    } = useProjectFormSetup();

    // Filter custom design accordions based on pathname - they should only appear on /results
    const groupedFields = useMemo(() => {
        if (location.pathname.includes('/designCondition')) {
            const filtered = { ...rawGroupedFields };
            delete filtered.surfaceDesign;
            delete filtered.baseDesign;
            delete filtered.subbaseDesign;
            return filtered;
        }
        return rawGroupedFields;
    }, [rawGroupedFields, location.pathname]);

    const { openAccordions, handleAccordionToggle } = useAccordionManagement();

    useFormSubmission(formikRef);

    // Consolidated form state management using centralized service
    const { updateFormState, isOnlyDesignSectionChange, canRedirect, shouldAutoSubmit } = useFormStateManagement();

    // 🔄 REFACTORED: ESAL modal management using centralized service
    const { isEsalModalOpen, esalModalKey, handleEsalModalClose, handleEsalCalculationComplete, openEsalModal } = useEsalModalManagement();

    // Use custom hooks for form state management
    const projectHasBeenRan = useAppSelector(state => state.Dynamic.project.data?.hasBeenRan);
    const dynamicProject = useAppSelector(state => state.Dynamic.project);

    // State for tracking Railway conditional defaults application
    const [isApplyingDefaults, setIsApplyingDefaults] = useState(false);
    const handleApplyingDefaultsChange = useCallback((isApplying: boolean) => {
        setIsApplyingDefaults(isApplying);
    }, []);

    // Form effects hook (handles submission side effects) - 🚀 REFACTORED: Now uses state machine
    // Removed isNavigatingRef parameter - now uses Redux flag which is properly reset after 500ms
    useFormEffects(canRedirect, shouldAutoSubmit, projectHasBeenRan, formikRef, isApplyingDefaults);

    // Material cascade hook will be initialized inside Formik render prop where formik props are available

    // Hook per la gestione dell'inizializzazione del material cascade
    const { initializeOnce, resetInitialization } = useMaterialCascadeInit();

    // Initialize material cascade hook
    const materialCascade = useFormCascade<ProjectFormValues>();

    // 🔧 FIX: Stabilized material cascade initialization usando useLayoutEffect
    // per evitare race conditions tra ref update e initialization
    useLayoutEffect(() => {
        if (dynamicProject.data) {
            resetInitialization();
        }
    }, [dynamicProject.data, resetInitialization]);

    // Enhanced input change handler with NEW CASCADE LOGIC
    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setFieldValue?: (field: string, value: unknown) => void, fieldName?: string, formValues?: Record<string, any>) => {
            const { value } = e.target;
            const currentMethod = projectDataFromState?.method;

            // Early return if required parameters are missing
            if (!setFieldValue || !fieldName) {
                return;
            }

            if (fieldName === 'calculationMethod' && currentMethod !== undefined && value !== currentMethod) {
                // Pass formValues so handleMethodChange can use current form data (user's unsaved edits)
                handleMethodChange(value, currentMethod, setFieldValue as (field: string, value: unknown) => void, formValues); // eslint-disable-line @typescript-eslint/no-explicit-any
                return;
            }

            // Check for ESAL calculation mode change to 'automatic'
            if (fieldName === 'esalCalculationMode' && value === 'automatic') {
                // Show modal for paved/asphalt projects and airport projects
                if ((projectDataFromState?.type === 'paved' && projectDataFromState?.method === 'asphalt') || projectDataFromState?.type === 'airport') {
                    //  REFACTORED: Use centralized service for ESAL modal management
                    openEsalModal({ setFieldValue: setFieldValue as (field: string, value: unknown) => void } as DynamicFormikProps); // eslint-disable-line @typescript-eslint/no-explicit-any
                }
            }

            // Surface material changes are now handled by NEW CASCADE SYSTEM via MaterialCascadeService
            // This eliminates double processing and potential race conditions during form submit

            // Handle ESAL value changes - automatically update minimum suggested thickness for surface
            if (fieldName === 'esalFinalValue' && typeof value === 'string') {
                const esalValue = parseFloat(value);
                if (!isNaN(esalValue)) {
                    const minSuggestedThickness = getMinimumSuggestedThicknessForSurface(esalValue);
                    // Update the minimum suggested thickness field in the surface group
                    (setFieldValue as (field: string, value: unknown) => void)('surface.minThicknessSuggested', minSuggestedThickness); // eslint-disable-line @typescript-eslint/no-explicit-any
                    logDebug('InputForm', `ESAL value changed to ${esalValue}, setting minimum suggested thickness to ${minSuggestedThickness}`, undefined, LogTag.CALCULATIONS);

                    // Also update base course minimum suggested thickness
                    // Use ESAL-based calculation directly from DATABASE.txt Table 26
                    const baseMinSuggestedThickness = getMinimumSuggestedThicknessForBase(esalValue);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (setFieldValue as (field: string, value: any) => void)('baseCourse.minThicknessSuggested', baseMinSuggestedThickness);
                    logDebug(
                        'InputForm',
                        `ESAL value changed to ${esalValue}, setting base minimum suggested thickness to ${baseMinSuggestedThickness} based on AASHTO traffic classification`,
                        undefined,
                        LogTag.CALCULATIONS
                    );

                    // Also update subbase course minimum suggested thickness
                    // This follows Design_LCR G26 logic which uses DATABASE!K16 VLOOKUP
                    const subbaseMinSuggestedThickness = getMinimumSuggestedThicknessForSubbase(esalValue);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (setFieldValue as (field: string, value: any) => void)('subbaseCourse.minThicknessSuggested', subbaseMinSuggestedThickness);
                    logDebug(
                        'InputForm',
                        `ESAL value changed to ${esalValue}, setting subbase minimum suggested thickness to ${subbaseMinSuggestedThickness} based on AASHTO traffic classification`,
                        undefined,
                        LogTag.CALCULATIONS
                    );
                }
            }

            // **🚨 NOTE:** Base and subbase material changes are now handled by the NEW CASCADE SYSTEM
            // This legacy code has been removed and replaced with the new cascade logic
            // See the enhanced Formik render function below for the new implementation

            // For other fields (including enable flags), Formik handles changes automatically
            // useStepperNavigation now reads hasBeenRan directly from Redux to avoid props sync lag
        },
        [projectDataFromState?.method, projectDataFromState?.type, projectDataFromState, handleMethodChange, openEsalModal, dispatch]
    );

    const getMeasurementUnit = useCallback(
        (unitArray: string[] | undefined): string => {
            if (!unitArray) return '';
            const index = projectUnitFromState === unitEnum.SI ? 0 : 1;
            return unitArray.length > index ? ` (${unitArray[index]})` : '';
        },
        [projectUnitFromState]
    );

    // Early returns for loading states
    // With callback pattern in useProjectLoader, validationSchema is set before loading=false
    if (loading && !isMethodChanging) {
        return <div>Loading...</div>;
    }

    if (isMethodChanging && !confirmDialogState?.isOpen) {
        return <div>Loading new method...</div>;
    }

    // Show error only when there's an actual error message
    if (errorDialog && !validationSchema) {
        return (
            <div>
                <h4>Error</h4>
                <p>{t(errorDialog) || 'Failed to load form configuration.'}</p>
            </div>
        );
    }

    const displayType = projectDataFromState?.type || 'N/A';
    const displayMethod = projectDataFromState?.method || 'N/A';

    return (
        <Box sx={{ px: 4, py: 2, maxWidth: 700, margin: '0 auto' }}>
            <div>
                <div className="form-header">
                    <h3>
                        <span className="fw-bold">{t(displayType)}</span>
                    </h3>
                    <h4>{t('project_data')}</h4>
                </div>
            </div>

            {validationSchema && groupedFields && Object.keys(groupedFields).length > 0 && (
                // 🔍 DEBUG: Log validatedData when Formik renders
                console.log('🔍 DEBUG: Formik rendering with validatedData', {
                    surfaceMinThickness: (validatedData as any)?.surface?.minThickness,
                    validatedDataKeys: Object.keys(validatedData || {}),
                }),
                <Formik
                    innerRef={formikRef}
                    key={`${displayMethod}-${projectDataFromState?.guid || 'new'}`} // Stable key based on method and project GUID
                    initialValues={validatedData}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting, resetForm, setStatus }) => {
                        try {
                            logDebug('DynamicForm onSubmit', 'Validation OK - Nested', values, LogTag.FORM_SUBMISSION);

                            // Check if this submit is only for design section changes
                            const isOnlyDesignChange = isOnlyDesignSectionChange(values, validatedData);
                                // Read on entry: submitSucceeded consumes the source, and the design autosubmit has none
                                const isExplicitSave = redux.getState().formControl.lastRequestSource === 'ButtonSave';

                            logDebug(
                                'DynamicForm onSubmit',
                                'Submit type analysis:',
                                {
                                    isOnlyDesignChange,
                                    hasBeenRan: projectHasBeenRan,
                                    submitType: isOnlyDesignChange ? 'design-only' : 'full-submit',
                                },
                                LogTag.FORM_SUBMISSION
                            );

                            // Update the project data in Redux with the form values
                            if (projectDataFromState) {
                                // 🔧 FIX: Check conversion state before applying Imperial-to-SI conversion
                                let processedValues = values;
                                let conversionApplied = false;
                                let conversionSource = '';

                                // Enhanced logging for form submission conversion decisions
                                logDebug(
                                    'FORM_SUBMIT',
                                    'Starting form submission conversion analysis:',
                                    {
                                        projectType: projectDataFromState.type,
                                        projectMethod: projectDataFromState.method,
                                        currentUnit: projectDataFromState.unit,
                                        originalUnit: projectDataFromState.originalUnit,
                                        conversionApplied: projectDataFromState.conversionApplied,
                                        conversionSource: projectDataFromState.conversionSource,
                                        targetUnit: unitEnum.SI,
                                        formValuePreview: Object.keys(values).slice(0, 5),
                                    },
                                    LogTag.UNIT_CONVERSION
                                );

                                // Check if conversion is needed using our enhanced logic
                                const conversionDecision = isConversionNeeded(
                                    unitEnum.SI, // Target: storage should be in SI
                                    projectDataFromState.unit, // Current: form unit
                                    projectDataFromState.conversionApplied || false,
                                    projectDataFromState.originalUnit
                                );

                                // Enhanced logging for Container Yard submissions (the bug-prone type)
                                if (projectDataFromState.type === 'container_yard') {
                                    logDebug(
                                        'CONTAINER_YARD_SUBMIT',
                                        'Container Yard form submission conversion decision:',
                                        {
                                            shouldConvert: conversionDecision.conversionNeeded,
                                            reason: conversionDecision.reason,
                                            confidence: conversionDecision.confidence,
                                            currentState: {
                                                unit: projectDataFromState.unit,
                                                originalUnit: projectDataFromState.originalUnit,
                                                conversionApplied: projectDataFromState.conversionApplied,
                                            },
                                            // Log specific values that were causing the bug
                                            valuePreview: values.generalSettings
                                                ? {
                                                      width: values.generalSettings.width,
                                                      length: values.generalSettings.length,
                                                  }
                                                : 'no generalSettings',
                                        },
                                        LogTag.UNIT_CONVERSION
                                    );
                                }

                                logConversionDecision(conversionDecision, `InputForm onSubmit (${projectDataFromState.type}/${projectDataFromState.method})`);

                                // Only convert if the decision logic says we should
                                if (conversionDecision.conversionNeeded && projectDataFromState.unit === unitEnum.Imperial && groupedFields && validationSchema) {
                                    logDebug('DynamicForm onSubmit', 'Converting Imperial form values back to SI for storage', undefined, LogTag.UNIT_CONVERSION);

                                    // Get flat field configurations for conversion
                                    const flatFields: FieldConfig[] = [];
                                    Object.values(groupedFields).forEach(group => {
                                        flatFields.push(...group);
                                    });

                                    // Convert form values from Imperial back to SI for storage
                                    // FLAT structure: convert directly without wrapping
                                    const convertedValues = convertFlatData(
                                        values,
                                        flatFields,
                                        unitEnum.Imperial, // Source: form is in Imperial
                                        unitEnum.SI // Target: storage should be in SI
                                    );

                                    if (convertedValues) {
                                        processedValues = convertedValues;
                                        conversionApplied = true;
                                        conversionSource = FORM_SUBMIT_CONVERSION_SOURCE;
                                        logDebug('DynamicForm onSubmit', 'Successfully converted Imperial values back to SI:', processedValues, LogTag.UNIT_CONVERSION);
                                    } else {
                                        logWarn('DynamicForm onSubmit', 'Failed to convert Imperial values back to SI, using original values', undefined, LogTag.UNIT_CONVERSION);
                                    }
                                } else {
                                    logDebug(
                                        'DynamicForm onSubmit',
                                        'Skipping conversion based on state analysis:',
                                        {
                                            conversionDecision,
                                            currentUnit: projectDataFromState.unit,
                                            reason: conversionDecision.reason,
                                        },
                                        LogTag.UNIT_CONVERSION
                                    );
                                }

                                // ✅ CRITICAL FIX (BUG-003): Remove stale nested [method] key before merge
                                // REASON: Backend may have saved data in nested format (e.g., {asphalt: {...}})
                                // When reloaded, projectInfo.data contains nested structure from previous save.
                                // If we just spread {...projectInfo.data, ...processedValues}, we get BOTH:
                                //   - Nested (stale): {asphalt: {baseGeogrid: "EG 20S"}}
                                //   - Flat (new): {baseCourse: {baseGeogrid: "EG 40S"}}
                                // BackendPayloadService finds nested → uses stale data → DATA LOSS!
                                //
                                // SOLUTION: Explicitly remove [method] key before merging flat values
                                //
                                // ⚠️ ARCHITECTURE NOTE (v5.1.0+):
                                // - ALL projects (including Railway): Redux stores FLAT format { roadInfo, trafficLoad, ... } (PD4)
                                // - Railway no longer uses NESTED .default wrapper in Redux
                                // - prepareProjectPayload() transforms FLAT → NESTED before backend save
                                // - transformProjectFromBackend() transforms backend NESTED → Redux FLAT on load
                                //
                                // Type Safety: See ProjectDataStructure.ts for:
                                // - isNestedStructure() - Detect if data is nested
                                // - ensureFlatStructure() - Guarantee FLAT output for Redux
                                // - ensureNestedStructure() - Transform FLAT → NESTED for backend
                                //
                                // @see ProjectDataStructure.ts for type guards
                                // @see ProjectApiService.ts for FLAT vs NESTED architecture documentation

                                // ✅ UNIFIED DATA STRUCTURE (v5.1.0+): All projects use FLAT structure
                                // Railway no longer uses NESTED .default wrapper
                                // Common fields (designerFirmCity, nrProject, notes, geoGrid) preserved from Redux
                                const existingData = projectDataFromState.projectInfo.data;
                                const method = projectDataFromState.method;

                                // Remove stale nested [method] key if exists (for Paved "asphalt", Railway "default", etc.)
                                const { [method]: _removed, ...cleanExistingData } = existingData as any;

                                // 🔍 DEBUG: Trace surface.minThickness values for form reset bug
                                console.log('🔍 DEBUG: Form submission data merge', {
                                    surfaceMinThicknessInExistingData: (existingData as any)?.surface?.minThickness,
                                    surfaceMinThicknessInCleanExistingData: (cleanExistingData as any)?.surface?.minThickness,
                                    surfaceMinThicknessInProcessedValues: (processedValues as any)?.surface?.minThickness,
                                    surfaceMinThicknessInFormValues: (values as any)?.surface?.minThickness,
                                    conversionApplied,
                                    conversionNeeded: conversionDecision.conversionNeeded,
                                    projectUnit: projectDataFromState.unit,
                                });

                                const dataToSave = {
                                    ...cleanExistingData, // Existing data WITHOUT nested [method] key (preserves common fields)
                                    ...processedValues, // New form values (flat)
                                };

                                // 🔍 DEBUG: Check the merged result
                                console.log('🔍 DEBUG: After merge dataToSave', {
                                    surfaceMinThicknessInDataToSave: (dataToSave as any)?.surface?.minThickness,
                                    hasSurfaceGroup: 'surface' in dataToSave,
                                    surfaceKeys: Object.keys((dataToSave as any)?.surface || {}),
                                });

                                const updatedProject = {
                                    ...projectDataFromState,
                                    // 🔧 FIX: Exclude transient navigation flag from form submission
                                    // isNavigatingToResults is set by useCalculationOrchestrator during RUN and
                                    // cleared by Results.tsx on mount. If we preserve it from projectDataFromState,
                                    // it can re-persist a stale 'true' value, blocking hasBeenRan reset and redirect
                                    // when user edits non-design fields on Results page.
                                    isNavigatingToResults: undefined,
                                    projectInfo: {
                                        ...projectDataFromState.projectInfo,
                                        data: dataToSave,
                                    },
                                    // Update conversion tracking if conversion was applied
                                    ...(conversionApplied && {
                                        originalUnit: projectDataFromState.originalUnit || projectDataFromState.unit,
                                        conversionApplied: true,
                                        conversionSource,
                                    }),
                                    lastModified: new Date().toISOString(),
                                };

                                logDebug(
                                    'DynamicForm onSubmit',
                                    'Updating project with form values:',
                                    {
                                        originalProject: projectDataFromState,
                                        formValues: values,
                                        updatedProject,
                                    },
                                    LogTag.DATA_TRANSFORM
                                );

                                // 🔧 FIX: Sequenced dispatch calls per evitare race conditions
                                dispatch(setProject(updatedProject as any));

                                // Additional logging for design-only changes
                                if (isOnlyDesignChange && projectHasBeenRan) {
                                    logDebug('DynamicForm onSubmit', 'Design-only update completed - results should re-render automatically', undefined, LogTag.FORM_SUBMISSION);
                                }

                                // Only call submitSucceeded for non-design-only changes or an explicit Save
                                // Design-only changes should just update the project data without triggering submission flow
                                if (isExplicitSave || !isOnlyDesignChange || !projectHasBeenRan) {
                                    // ✅ FIX: Pass processedValues (already SI) instead of values (raw Imperial)
                                    // This ensures Stepper receives data in same format as Redux
                                    dispatch(submitSucceeded(processedValues));
                                } else {
                                    logDebug('DynamicForm onSubmit', 'Skipping submitSucceeded for design-only changes', undefined, LogTag.FORM_SUBMISSION);
                                }
                            } else {
                                throw new Error('projectDataFromState is null, cannot update project');
                            }

                            // By resetting the form with the current values, we are telling Formik
                            // that this is the new "pristine" state. This sets `dirty` to false.
                            resetForm({ values });
                            setStatus(null); // Clear any previous errors
                        } catch (error) {
                            // 🔧 FIX: Proper error handling
                            dispatch(submitFailed({ error: createErrorPayload(error) }));
                            setStatus({ error: error instanceof Error ? error.message : 'Unknown error occurred' });
                        } finally {
                            setSubmitting(false);
                        }
                    }}>
                    {formikProps => {
                        // Type assertion needed for ref assignment in Formik render prop
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (materialCascadeFormikRef as React.MutableRefObject<any>).current = formikProps;

                        return (
                            <FormikInnerComponent
                                {...formikProps}
                                updateFormState={updateFormState}
                                materialCascade={{
                                    handleBaseMaterialChange: (value: string) => materialCascade.handleBaseMaterialChange(value, formikProps.setFieldValue, formikProps.values),
                                    handleSubbaseMaterialChange: (value: string) => materialCascade.handleSubbaseMaterialChange(value, formikProps.setFieldValue, formikProps.values),
                                    handleSurfaceMaterialChange: (value: string) => materialCascade.handleSurfaceMaterialChange(value, formikProps.setFieldValue, formikProps.values),
                                    handleBaseGeogridChange: (value: string) => materialCascade.handleBaseGeogridChange(value, formikProps.setFieldValue, formikProps.values),
                                    handleSubbaseGeogridChange: (value: string) => materialCascade.handleSubbaseGeogridChange(value, formikProps.setFieldValue, formikProps.values),
                                    handleRailwayGeogridChange: (value: string) => materialCascade.handleRailwayGeogridChange(value, formikProps.setFieldValue),
                                    initializeFormMaterials: () => materialCascade.initializeFormMaterials(formikProps.setFieldValue, formikProps.values),
                                    // New centralized handlers
                                    handleFieldChange: (fieldName: string, value: string, setFieldValue: (field: string, value: any) => void, values?: any) =>
                                        materialCascade.handleFieldChange(fieldName, value, setFieldValue, values),
                                    isFieldCascadable: (fieldName: string) => materialCascade.isFieldCascadable(fieldName),
                                }}
                                initializeOnce={initializeOnce}
                                dynamicProject={dynamicProject}
                                handleInputChange={handleInputChange}
                                projectDataFromState={projectDataFromState}
                                projectUnitFromState={projectUnitFromState || 'SI'}
                                validatedData={validatedData}
                                getMeasurementUnit={getMeasurementUnit}
                                handleAccordionToggle={handleAccordionToggle}
                                handleEsalModalClose={handleEsalModalClose}
                                handleEsalCalculationComplete={handleEsalCalculationComplete}
                                groupedFields={groupedFields}
                                displayMethod={displayMethod}
                                accordionTitles={accordionTitles}
                                t={t}
                                isNewProject={isNewProject}
                                openAccordions={openAccordions}
                                registerMethodSyncCallback={registerMethodSyncCallback}
                                updateGroupedFieldsForDesignChange={updateGroupedFieldsForDesignChange}
                                onApplyingDefaultsChange={handleApplyingDefaultsChange}
                                dispatch={dispatch}
                            />
                        );
                    }}
                </Formik>
            )}

            {/* Confirmation Dialog for Method Change */}
            {confirmDialogState?.isOpen && (
                <Dialog
                    open={confirmDialogState.isOpen}
                    title={t('dialog_attention') || confirmDialogState.title}
                    hideCloseButton={true}
                    onClose={() => {
                        if (confirmDialogState.onCancel) {
                            confirmDialogState.onCancel();
                        }
                    }}
                    actions={[
                        {
                            text: t('cancel_button_label', 'Cancel'),
                            callback: () => {
                                if (confirmDialogState.onCancel) {
                                    confirmDialogState.onCancel();
                                }
                            },
                            color: 'error',
                        },
                        {
                            text: t('confirm_button_label', 'Confirm'),
                            callback: () => {
                                if (confirmDialogState.onConfirm) {
                                    confirmDialogState.onConfirm();
                                }
                            },
                            color: 'success',
                        },
                    ]}>
                    <Typography variant="body1" sx={{ color: 'red', textAlign: 'center', fontSize: 20, marginBottom: 0, marginTop: 1 }}>
                        {t(confirmDialogState.message) || confirmDialogState.message}
                    </Typography>
                </Dialog>
            )}

            {/* Existing Error Dialog */}
            <Dialog
                open={!!errorDialog && errorDialog !== 'error_loading_project' && errorDialog !== 'error_setting_up_new_project' && errorDialog !== 'error_missing_project_id'}
                title={t('error_dialog_title') || 'Error'}
                onClose={() => setErrorDialog('')}>
                <div>{t(errorDialog) || errorDialog}</div>
            </Dialog>

            {/* 🔧 FIX: ESAL Calculation Modal con key per force remount */}
            {dynamicProject?.data?.type === 'airport' ? (
                <EsalCalculationModalAirport key={esalModalKey} open={isEsalModalOpen} onClose={handleEsalModalClose} onCalculationComplete={handleEsalCalculationComplete} />
            ) : (
                <EsalCalculationModal key={esalModalKey} open={isEsalModalOpen} onClose={handleEsalModalClose} onCalculationComplete={handleEsalCalculationComplete} />
            )}
        </Box>
    );
};

export default DynamicForm;
